<div class="legal-entity-container">
  
  <!-- Loading Spinner -->
  @if (loading) {
    <div class="loading-container">
      <mat-spinner diameter="60"></mat-spinner>
      <p>Loading legal entity...</p>
    </div>
  }

  @if (!loading) {
    <!-- Header Section -->
    <div class="header-section">
      <div class="title-section">
        <h1>Legal Entity Details</h1>
        @if (isInactive) {
          <mat-chip class="inactive-chip" color="warn">
            <mat-icon>cancel</mat-icon>
            INACTIVE
          </mat-chip>
        }
      </div>
      
      <div class="header-actions">
        <button mat-stroked-button (click)="goBack()">
          <mat-icon>arrow_back</mat-icon>
          Back to List
        </button>
        <button mat-raised-button color="primary" 
                [disabled]="!canEdit"
                (click)="editLegalEntity()">
          <mat-icon>edit</mat-icon>
          Edit
        </button>
      </div>
    </div>

    <h2 class="entity-name">{{ currentLegalEntity.legalEntityName }}</h2>

    <!-- Main Information Card -->
    <mat-card class="info-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>account_balance</mat-icon>
          Legal Entity Information
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="info-grid">
          
          <!-- Legal Entity Name -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Legal Entity Name</mat-label>
            <input matInput 
                   [value]="currentLegalEntity.legalEntityName"
                   readonly>
            <mat-icon matSuffix>business</mat-icon>
          </mat-form-field>

          <!-- Legal Entity Parent -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Legal Entity Parent</mat-label>
            <input matInput 
                   [value]="currentLegalEntity.legalEntityParent"
                   readonly>
            <mat-icon matSuffix>corporate_fare</mat-icon>
          </mat-form-field>

          <!-- Legal Entity Number -->
          <mat-form-field appearance="outline">
            <mat-label>Legal Entity Number</mat-label>
            <input matInput 
                   [value]="currentLegalEntity.legalEntityNum"
                   readonly>
            <mat-icon matSuffix>tag</mat-icon>
          </mat-form-field>

          <!-- FEIN -->
          <mat-form-field appearance="outline">
            <mat-label>FEIN</mat-label>
            <input matInput 
                   [value]="currentLegalEntity.fein"
                   readonly>
            <mat-icon matSuffix>badge</mat-icon>
          </mat-form-field>

          <!-- DOP Number (if applicable) -->
          @if (currentLegalEntity.dopNum) {
            <mat-form-field appearance="outline">
              <mat-label>DOP Number</mat-label>
              <input matInput 
                     [value]="currentLegalEntity.dopNum"
                     readonly>
              <mat-icon matSuffix>numbers</mat-icon>
            </mat-form-field>
          }

        </div>
      </mat-card-content>
    </mat-card>

    <!-- Address Information Card -->
    <mat-card class="info-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>place</mat-icon>
          Address Information
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="info-grid">
          
          <!-- Address -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Address</mat-label>
            <textarea matInput 
                      [value]="currentLegalEntity.address"
                      rows="2"
                      readonly>
            </textarea>
            <mat-icon matSuffix>home</mat-icon>
          </mat-form-field>

          <!-- City -->
          <mat-form-field appearance="outline">
            <mat-label>City</mat-label>
            <input matInput 
                   [value]="currentLegalEntity.city"
                   readonly>
            <mat-icon matSuffix>location_city</mat-icon>
          </mat-form-field>

          <!-- State -->
          <mat-form-field appearance="outline">
            <mat-label>State</mat-label>
            <input matInput 
                   [value]="currentLegalEntity.state"
                   readonly>
            <mat-icon matSuffix>map</mat-icon>
          </mat-form-field>

          <!-- ZIP -->
          <mat-form-field appearance="outline">
            <mat-label>ZIP Code</mat-label>
            <input matInput 
                   [value]="currentLegalEntity.zip"
                   readonly>
            <mat-icon matSuffix>markunread_mailbox</mat-icon>
          </mat-form-field>

        </div>
      </mat-card-content>
    </mat-card>

    <!-- Status Card -->
    <mat-card class="info-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>info</mat-icon>
          Status
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="status-container">
          <mat-slide-toggle 
            [checked]="isInactive"
            [disabled]="true">
            <span [class.active]="!isInactive" [class.inactive]="isInactive">
              @if (!isInactive) {
                <mat-icon>check_circle</mat-icon>
                Active
              } @else {
                <mat-icon>cancel</mat-icon>
                Inactive
              }
            </span>
          </mat-slide-toggle>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Comments Card -->
    @if (currentLegalEntity.comments) {
      <mat-card class="info-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>note</mat-icon>
            Comments & Notes
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="comments-display" [innerHTML]="currentLegalEntity.comments"></div>
        </mat-card-content>
      </mat-card>
    }

    <!-- Associated Contracts Card -->
    <mat-card class="info-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>description</mat-icon>
          Associated Contracts
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        @if (hasContracts) {
          <ag-grid-angular
            style="width: 100%; height: 400px;"
            class="ag-theme-balham"
            [rowData]="contractsAssociatedWithThisLegalEntity"
            [columnDefs]="contractCols"
            [defaultColDef]="defaultColDef"
            (gridReady)="onGridReady($event)"
            (cellClicked)="onCellClicked($event)">
          </ag-grid-angular>
        } @else {
          <div class="no-contracts">
            <mat-icon>folder_open</mat-icon>
            <p>No contracts associated with this legal entity</p>
          </div>
        }
      </mat-card-content>
    </mat-card>

  }

</div>